import React from 'react';
import { Scale, Users, Shield } from 'lucide-react';

export function AboutSection() {
  return (
    <section id="about" className="py-12 sm:py-16 lg:py-20" aria-labelledby="about-title">
      <div className="container mx-auto px-4 sm:px-6">
        <h2 id="about-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 lg:hidden text-center">Meet Yehuda</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="lg:sticky lg:top-8">
            <div className="image-container w-full max-w-2xl mx-auto lg:max-w-none h-[800px]" data-description="Attorney Yehuda in his office with Israeli flag and legal books">
              <img 
                src="/public/yehuda-office.jpg"
                alt="Attorney Yehuda in his office with Israeli flag and legal books" 
                className="rounded-lg shadow-xl w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="mt-6 lg:mt-8 space-y-4 bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-2xl mx-auto lg:max-w-none">
              {[
                { icon: Scale, text: 'Expert Legal Services', desc: 'Comprehensive legal support across multiple practice areas' },
                { icon: Users, text: 'Cultural Bridge', desc: 'Seamless navigation between American and Israeli systems' },
                { icon: Shield, text: 'Comprehensive Support', desc: 'Full-service assistance beyond traditional legal work' }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 sm:space-x-4">
                  <div className="bg-blue-100 p-2 rounded-full shrink-0">
                    <item.icon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{item.text}</h3>
                    <p className="text-sm text-gray-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 hidden lg:block">Meet Yehuda</h2>
            
            <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
              <p className="text-gray-600">
                Yehuda Knobloch brings a unique blend of legal expertise, business acumen, and cross-cultural understanding. As an attorney with an LLB and MBA, Yehuda offers clients more than just legal documentation – he provides comprehensive guidance through Israel's complex legal landscape, business environment, and bureaucratic systems.
              </p>
              
              <p className="text-gray-600">
                With extensive experience managing international business operations and successfully growing client bases, Yehuda understands the challenges facing foreign businesses and individuals navigating unfamiliar territory. His fluency in English and Hebrew enables seamless communication with international clients while effectively interfacing with local authorities and institutions.
              </p>
              
              <p className="text-gray-600 font-medium">
                Yehuda's background spans multiple disciplines:
              </p>
              
              <ul className="list-disc pl-4 sm:pl-6 space-y-2 text-gray-600 my-4">
                <li>Entrepreneurship in the startup world</li>
                <li>Extensive experience managing international business operations</li>
                <li>Self-owned property construction and management</li>
                <li>High-level military leadership coordinating complex operations</li>
              </ul>
              
              <p className="text-gray-600">
                This diverse experience has honed his ability to analyze risks, identify strategic opportunities, and create practical solutions for clients across various sectors.
              </p>
              
              <p className="text-gray-600">
                Beyond his professional qualifications, Yehuda's commitment to community service through his non-profit organization and pro bono legal work for vulnerable populations demonstrates his dedication to ethical practice and social responsibility – values that inform his approach to client representation.
              </p>
              
              <p className="text-gray-600">
                Whether you're establishing a business presence in Israel, navigating real estate transactions, or managing regulatory compliance, Yehuda's comprehensive knowledge of Israeli systems and international business practices ensures your interests are protected by someone who truly understands both worlds.
              </p>
            </div>

            <div className="mt-6 sm:mt-8 bg-blue-50 p-4 sm:p-6 rounded-lg">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Comprehensive Support Packages</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Beyond traditional legal services, we provide comprehensive support packages tailored to your specific needs. Whether you're buying property, starting a business, or dealing with personal matters, we ensure you receive the best possible outcome.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}